import { 
  getHealthParamCacheKey, 
  isCacheValid, 
  CACHE_CONFIGS,
  generateCacheKey
} from '../../utils/cacheUtils';
import { 
  setCacheEntry, 
  updateCacheStats,
  cleanupExpiredEntries,
  clearCacheType
} from '../cache/cacheSlice';
import {
  getBloodSugarSuccess,
  getBloodPressureSuccess,
  getBloodOxygenSuccess,
  getBodyTempSuccess,
  getHeartRateSuccess,
  getRespiratoryRateSuccess,
  getHealthParamSuccess,
  getLipidSuccess
} from '../user/userSlice';
import {
  getpatientAppointmentListSuccess
} from '../appointment/patient/patientAppointmentSlice';

// Mapping of actions to their cache configurations
const ACTION_CACHE_CONFIG = {
  'user/patient/sugar': {
    cacheType: 'healthParams',
    successAction: getBloodSugarSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('bloodSugar', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/pressure': {
    cacheType: 'healthParams',
    successAction: getBloodPressureSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('bloodPressure', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/oxygen': {
    cacheType: 'healthParams',
    successAction: getBloodOxygenSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('bloodOxygen', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/temperature': {
    cacheType: 'healthParams',
    successAction: getBodyTempSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('bodyTemp', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/heart': {
    cacheType: 'healthParams',
    successAction: getHeartRateSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('heartRate', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/respiratory': {
    cacheType: 'healthParams',
    successAction: getRespiratoryRateSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('respiratoryRate', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient/profile': {
    cacheType: 'healthParams',
    successAction: getHealthParamSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('healthParams', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'user/patient': {
    cacheType: 'healthParams',
    successAction: getHealthParamSuccess,
    getCacheKey: (payload) => getHealthParamCacheKey('healthParams', payload),
    config: CACHE_CONFIGS.HEALTH_PARAMS
  },
  'patient/appointment/list': {
    cacheType: 'appointments',
    successAction: getpatientAppointmentListSuccess,
    getCacheKey: (payload) => generateCacheKey('appointments', {
      status: payload.status,
      userType: localStorage.getItem("userType")
    }),
    config: CACHE_CONFIGS.APPOINTMENTS
  },
  // Profile cache configuration
  'auth/getProfile': {
    cacheType: 'profiles',
    getCacheKey: (payload) => generateCacheKey('profile', {
      userType: localStorage.getItem("userType"),
      userId: payload.userId || 'current'
    }),
    config: CACHE_CONFIGS.PROFILES
  }
};

// Actions that should trigger cache invalidation
const CACHE_INVALIDATING_ACTIONS = {
  // When new health data is added manually
  'user/add-health-parameter': ['healthParams'],
  'user/addHealthParameter': ['healthParams'],
  'user/updateHealthParameter': ['healthParams'],
  
  // Blood Sugar related updates
  'user/addBloodSugar': ['healthParams'],
  'user/updateBloodSugar': ['healthParams'],
  
  // Blood Pressure related updates
  'user/addBloodPressure': ['healthParams'],
  'user/updateBloodPressure': ['healthParams'],
  
  // Heart Rate related updates
  'user/addHeartRate': ['healthParams'],
  'user/updateHeartRate': ['healthParams'],
  
  // Body Temperature related updates
  'user/addBodyTemperature': ['healthParams'],
  'user/updateBodyTemperature': ['healthParams'],
  
  // Blood Oxygen related updates
  'user/addBloodOxygen': ['healthParams'],
  'user/updateBloodOxygen': ['healthParams'],
  
  // Respiratory Rate related updates
  'user/addRespiratoryRate': ['healthParams'],
  'user/updateRespiratoryRate': ['healthParams'],
  
  // Generic health parameter actions
  'healthParam/add': ['healthParams'],
  'healthParam/update': ['healthParams'],
  'healthParam/delete': ['healthParams'],
  
  // When appointments are updated
  'appointment/update': ['appointments'],
  'appointment/cancel': ['appointments'],
  'appointment/create': ['appointments'],
  'appointment/reschedule': ['appointments'],
  'appointment/book': ['appointments'],
  'appointment/cancelAppointmentSuccess': ['appointments'],
  
  // Lipid profile updates
  'user/updateLipidProfile': ['lipidProfile'],
  'lipidProfile/update': ['lipidProfile'],
  
  // Medical history updates
  'user/updateMedicalHistory': ['medicalHistory'],
  'medicalHistory/update': ['medicalHistory'],
  
  // Patient profile updates - only clear profiles cache (targeted)
  'patient/update-patient': ['profiles'],
  'user/updatePatientProfile': ['profiles'],
  'user/updateProfile': ['profiles'], 
  'patient/updateProfile': ['profiles'],
  'auth/updateProfile': ['profiles'],
  
  // Additional profile update patterns (catch-all)
  'UPDATE_PATIENT_PROFILE': ['profiles'],
  'PATIENT_UPDATE_SUCCESS': ['profiles'],
  'PATIENT_PROFILE_UPDATE_SUCCESS': ['profiles'],
  'auth/updatePatientProfileSuccess': ['profiles'],
  'patient/updatePatientSuccess': ['profiles'],
  'user/updatePatientSuccess': ['profiles'],
  'patient/update-patient-success': ['profiles'],
  'profile/update': ['profiles'],
  'profile/updateSuccess': ['profiles'],
  
  // Clear all cache for any profile-related success action
  'auth/getPatientProfileSuccess': [], // Don't clear on get success, but track it
  
  // When user logs out
  'auth/logout': ['healthParams', 'appointments', 'lipidProfile', 'medicalHistory', 'profiles'],
  
  // When user switches profile (for multi-user scenarios)
  'auth/switch-user': ['healthParams', 'appointments', 'lipidProfile', 'medicalHistory'],
  
  // Force refresh actions (bypass cache)
  'user/forceRefreshHealthParams': ['healthParams'],
  'user/forceRefreshAll': ['healthParams', 'appointments', 'lipidProfile', 'medicalHistory'],
  'user/patient/deleteHealthParams': ['healthParams'],
  'user/patient/deleteHealthParamsSuccess': ['healthParams'],
  'INVALIDATE_HEALTH_PARAM_CACHE': ['healthParams'],
};

/**
 * Cache Middleware for Redux
 * Intercepts actions to check cache before API calls and caches responses
 */
const cacheMiddleware = (store) => (next) => (action) => {
  const state = store.getState();
  
  // Handle cache invalidating actions
  if (CACHE_INVALIDATING_ACTIONS[action.type]) {
    const cacheTypesToClear = CACHE_INVALIDATING_ACTIONS[action.type];
    cacheTypesToClear.forEach(cacheType => {
      store.dispatch(clearCacheType({ cacheType }));
    });
  }
  
  // Targeted catch-all for profile updates ONLY (not all cache)
  const actionType = action.type.toLowerCase();
  if ((actionType.includes('update') && (actionType.includes('patient') || actionType.includes('profile'))) ||
      actionType.includes('patient/update') ||
      actionType.includes('profile/update')) {
    
    console.log('🔄 Cache cleared for profile update action:', action.type);
    
    // Only clear profiles cache for profile updates (not all cache types)
    store.dispatch(clearCacheType({ cacheType: 'profiles' }));
  }
  
  // Handle cacheable actions
  const cacheConfig = ACTION_CACHE_CONFIG[action.type];
  if (cacheConfig && action.payload) {
    const { cacheType, successAction, getCacheKey, config } = cacheConfig;
    const cacheKey = getCacheKey(action.payload);
    
    // Check if we have valid cached data
    const cachedEntry = state.cache[cacheType] && state.cache[cacheType][cacheKey];
    
    if (cachedEntry && isCacheValid(cachedEntry, config.expiry)) {
      // Cache hit - dispatch success action with cached data
      store.dispatch(updateCacheStats({ type: 'hit' }));
      store.dispatch(successAction(cachedEntry.data));
      
      // Don't proceed with the original action (prevent API call)
      return;
    } else {
      // Cache miss - track it and let the action proceed
      store.dispatch(updateCacheStats({ type: 'miss' }));
    }
  }
  
  // Proceed with the original action
  const result = next(action);
  
  // Handle successful responses that should be cached
  handleSuccessResponseCaching(store, action);
  
  // Periodically cleanup expired cache entries
  schedulePeriodicCleanup(store);
  
  return result;
};

/**
 * Handle caching of successful API responses
 */
const handleSuccessResponseCaching = (store, action) => {
  // Cache successful health parameter responses
  const healthParamSuccessActions = [
    'user/getBloodSugarSuccess',
    'user/getBloodPressureSuccess',
    'user/getBloodOxygenSuccess',
    'user/getBodyTempSuccess',
    'user/getHeartRateSuccess',
    'user/getRespiratoryRateSuccess',
    'user/getHealthParamSuccess'
  ];
  
  if (healthParamSuccessActions.includes(action.type) && action.payload) {
    // We need to reconstruct the original request parameters to create the cache key
    // This is a limitation - ideally we'd pass this info along with the success action
    const state = store.getState();
    
    // For now, we'll cache based on the current user and timestamp
    const userType = localStorage.getItem("userType") || 'patient';
    const cacheKey = `${action.type}_${userType}_${Date.now()}`;
    
    store.dispatch(setCacheEntry({
      cacheType: 'healthParams',
      key: cacheKey,
      data: action.payload
    }));
  }
  
  // Cache appointment list responses
  if (action.type === 'appointment/getpatientAppointmentListSuccess' && action.payload) {
    const userType = localStorage.getItem("userType") || 'patient';
    const cacheKey = generateCacheKey('appointments', { userType });
    
    store.dispatch(setCacheEntry({
      cacheType: 'appointments',
      key: cacheKey,
      data: action.payload
    }));
  }
  
  // Cache lipid profile responses
  if (action.type === 'user/getLipidSuccess' && action.payload) {
    const userType = localStorage.getItem("userType") || 'patient';
    const cacheKey = generateCacheKey('lipidProfile', { userType });
    
    store.dispatch(setCacheEntry({
      cacheType: 'lipidProfile',
      key: cacheKey,
      data: action.payload
    }));
  }
};

/**
 * Schedule periodic cleanup of expired cache entries
 */
let lastCleanup = Date.now();
const CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

const schedulePeriodicCleanup = (store) => {
  const now = Date.now();
  
  if (now - lastCleanup > CLEANUP_INTERVAL) {
    lastCleanup = now;
    
    // Cleanup expired entries for each cache type
    Object.entries(CACHE_CONFIGS).forEach(([cacheType, config]) => {
      store.dispatch(cleanupExpiredEntries({
        cacheType: cacheType.toLowerCase().replace('_', ''),
        expiryTime: config.expiry
      }));
    });
  }
};

/**
 * Higher-order function to create cache-aware API call
 * This can be used in sagas to check cache before making API calls
 */
/**
 * Higher-order function to create cache-aware API call
 * This can be used in sagas to check cache before making API calls
 * Note: This function requires importing saga effects where it's used
 */
export const createCacheAwareSaga = (cacheType, getCacheKey, apiCall, config = {}) => {
  return function* (action) {
    // Import saga effects in the actual saga file where this is used
    const { select, put, call } = require('redux-saga/effects');
    
    const state = yield select();
    const cacheKey = getCacheKey(action.payload);
    const cachedEntry = state.cache[cacheType] && state.cache[cacheType][cacheKey];
    
    // Check cache first
    if (cachedEntry && isCacheValid(cachedEntry, config.expiry)) {
      yield put(updateCacheStats({ type: 'hit' }));
      return cachedEntry.data;
    }
    
    // Cache miss - make API call
    yield put(updateCacheStats({ type: 'miss' }));
    try {
      const apiResponse = yield call(apiCall, action.payload);
      
      if (apiResponse.response_type === "success") {
        const data = apiResponse.response.data;
        
        // Cache the response
        yield put(setCacheEntry({
          cacheType,
          key: cacheKey,
          data
        }));
        
        return data;
      }
      
      throw new Error(apiResponse.response.message || 'API call failed');
    } catch (error) {
      console.error('Cache-aware API call failed:', error);
      throw error;
    }
  };
};

// Backward compatibility export
export const withCache = createCacheAwareSaga;

export default cacheMiddleware;
